import axios from "axios";
import { UserModel, ConvertUser } from "../models/UserModel";
import { toast } from "react-toastify";
import { saveUserToLocalStorage } from "./Login_service";

const API_URL = process.env.REACT_APP_API_URL;

export const getAllClients = async (): Promise<UserModel[]> => {
  try {
    const token = localStorage.getItem("token");
    const parentId = localStorage.getItem("userId");
    // console.log("DEBUG getAllClients - token exists:", !!token);
    // console.log("DEBUG getAllClients - parentId:", parentId);

    if (!token || !parentId) {
      console.warn("Utilisateur non connecté ou ID manquant");
      return [];
    }

    const params = new URLSearchParams({ parent: parentId });
    const url = `${API_URL}/user/client`;

    const response = await axios.get(url, {
      params,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      validateStatus: () => true,
    });

    if (response.status === 200 && response.data && response.data.users) {
      return response.data.users.map((u: any) =>
        ConvertUser.toUserModel(JSON.stringify(u))
      );
    } else if (response.status === 401) {
      console.warn("Token invalide, suppression du token");
      localStorage.removeItem("token");
      localStorage.removeItem("userId");
      return [];
    } else {
      console.error("getAllClients response non-200:", response.status, response.data);
      toast.error(response.data?.message || `Erreur ${response.status}`);
      return [];
    }
  } catch (error: any) {
    console.error("Erreur dans getAllClients:", error);
    return [];
  }
};

export const getAllReferredClients = async (): Promise<UserModel[]> => {
  try {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("userId");
    // console.log("DEBUG getAllReferredClients - token exists:", !!token);

    if (!token || !userId) {
      console.warn("Utilisateur non connecté ou token manquant");
      return [];
    }

    const url = `${API_URL}/user/referred-clients`;

    const response = await axios.get(url, {
      params: { userIdInToken: userId },
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      validateStatus: () => true,
    });

    // console.log("DEBUG getAllReferredClients - status:", response.status, "data:", response.data);

    if (response.status === 200 && response.data && response.data.users) {
      return response.data.users.map((u: any) =>
        ConvertUser.toUserModel(JSON.stringify(u))
      );
    } else {
      console.error("getAllReferredClients response non-200:", response.status, response.data);
      toast.error(response.data?.message || `Erreur ${response.status}`);
      return [];
    }
  } catch (error: any) {
    console.error("Erreur dans getAllReferredClients:", error);
    if (axios.isAxiosError(error) && error.response) {
      console.error("error.response.status:", error.response.status);
      console.error("error.response.data:", error.response.data);
    }
    return [];
  }
};

export const getUserDetails = async (userId?: number): Promise<UserModel | void> => {
  try {
    const currentUserId = userId || localStorage.getItem("userId");
    const token = localStorage.getItem("token");

    if (!currentUserId || !token) {
      console.error("Utilisateur non connecté ou token manquant");
      return;
    }

    const response = await axios.get(`${API_URL}/user/${currentUserId}`, {
      params: { userIdInToken: localStorage.getItem("userId") },
      headers: {
        "Content-Type": "application/json",
        Authorization: `${token}`,
      },
      
    });
    // console.log("DEBUG getUserDetails - status:", response.status, "data:", response.data);


    if (response.status === 200) {
      // Conversion avec valeurs par défaut
      const user = ConvertUser.toUserModel(JSON.stringify(response.data.data));

      return user;
    } else {
      toast.error(`Erreur: ${response.data.message}`);
    }
  } catch (error) {
    console.error("Erreur détaillée:", error);
    toast.error("Erreur lors de la récupération des informations");
    throw error;
  }
};

export const changeUserRemise = async (
  userIdChange: number,
  remise: number
): Promise<void> => {
  try {
    const userId = localStorage.getItem("userId");
    const token = localStorage.getItem("token");

    if (!userId) {
      toast.error("Utilisateur non connecté");
      return;
    }

    if (!token) {
      toast.error("Token non trouvé");
      return;
    }

    const response = await axios.post(
      `${API_URL}/user/update-remise/${userIdChange}`,
      {
        remise: remise, // Le corps de la requête, contenant la remise à mettre à jour
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `${token}`,
        },
      }
    );

    const responseBody = response.data;

    if (response.status === 200) {
      // console.log("RESPONSE ", responseBody);
    } else {
      toast.error(`${JSON.stringify(responseBody.message)}`);
    }
  } catch (error) {
    toast.error(
      "Erreur lors de la récupération des informations de l'utilisateur"
    );
    if (axios.isAxiosError(error) && error.response) {
      throw error.response.data;
    } else {
      throw new Error("Une erreur inconnue s'est produite");
    }
  }
};

export const updateUserInfoWithFiles = async (
  userId: number,
  changes: Record<string, any>,
  files?: {
    kbisFile?: File;
    identityRecto?: File;
    identityVerso?: File;
    identityType?: 'cin' | 'passeport';
    ribFile?: File; // ✅ AJOUT du support RIB
  },
  onUploadProgress?: (progressEvent: any) => void
) => {
  try {
    const formData = new FormData();

    // Ajouter les changements de données
    Object.entries(changes).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value);
      }
    });

    // Ajouter les fichiers si présents
    if (files?.kbisFile) {
      formData.append('kbis_document', files.kbisFile);
    }
    if (files?.identityRecto) {
      formData.append('identity_document_recto', files.identityRecto);
    }
    if (files?.identityVerso) {
      formData.append('identity_document_verso', files.identityVerso);
    }
    if (files?.identityType) {
      formData.append('identity_document_type', files.identityType);
    }
    // ✅ AJOUT : Gestion du fichier RIB
    if (files?.ribFile) {
      formData.append('rib_document', files.ribFile);
    }

    // Indiquer que c'est une modification par l'admin (pour déclencher les notifications)
    formData.append('admin_modification', 'true');

    const response = await axios.put(
      `${API_URL}/user/${userId}/update-with-files`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        onUploadProgress
      }
    );

    return response;
  } catch (error: any) {
    console.error('Erreur updateUserInfoWithFiles:', error);
    throw error;
  }
};

export const updateUserInfo = async (userId: number, userData: any) => {
  try {
    // ✅ Calculer is_entreprise automatiquement si user_type change
    if (userData.user_type) {
      userData.is_entreprise = 
        userData.user_type === "entreprise" || 
        userData.user_type === "professionnel";
    }

    const response = await axios.put(`${API_URL}/user/${userId}`, userData, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });

    return response;
  } catch (error) {
    console.error("Erreur lors de la mise à jour des informations utilisateur", error);
    throw error;
  }
};

export const verifyToken = async (token: string) => {
  const response = await axios.post(`${API_URL}/user/verify-token`, { token });
  return response.data;
};

export const verifyEmail = async (token: string) => {
  const response = await axios.post(`${API_URL}/user/verify-email`, { token });
  return response.data;
};

export const verify2FACode = async (code: string, email: string) => {
  try {
    const response = await axios.post(`${API_URL}/user/verify-two-fa-code`, {
      code,
      email,
    });
    if (response.status === 200 && response.data.user && response.data.token) {
      saveUserToLocalStorage(response.data.user, response.data.token);
    }
    return response.data;
  } catch (error: any) {
    toast.error(error?.response?.data?.message);
  }
};

export const getValidationRequestCount = async (): Promise<any> => {
  try {
    const userId = localStorage.getItem("userId");
    const token = localStorage.getItem("token");

    if (!token) {
      toast.error("Token non trouvé");
      return;
    }

    if (!userId) {
      toast.error("Utilisateur non connecté");
      return;
    }

    const params = new URLSearchParams({
      userIdInToken: userId!.toString(),
    });

    const response = await axios.get(
      `${API_URL}/user/validation-request-count`,
      {
        params,
        headers: {
          "Content-Type": "application/json",
          Authorization: `${token}`,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    console.error(error);
  }
};

export const resendCode = async () => {
  try {
    const email = localStorage.getItem("userEmail");

    if (!email) {
      throw new Error("Email non trouvé");
    }

    const response = await axios.post(`${API_URL}/user/resend-code`, { email });

    return response.data;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      // Handle specific backend error messages
      throw new Error(error.response.data.message || "Erreur inconnue");
    } else {
      // Handle other errors
      throw error.message || "Une erreur inconnue s'est produite.";
    }
  }
};

export const acceptCommitment = async (userId: number | null): Promise<any> => {
  try {
    if (!userId) {
      toast.error("Erreur lors de l'acceptation de l'engagement.");
    }

    const response = await axios.post(
      `${API_URL}/user/${userId}/commitment-accepted`,
      {},
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    return response

  } catch (error) {
    toast.error("Erreur lors de l'acceptation de l'engagement.");
    if (axios.isAxiosError(error) && error.response) {
      console.error("Erreur:", error.response.data);
      throw new Error(error.response.data.message || "Erreur inconnue.");
    } else {
      throw new Error("Une erreur inconnue s'est produite.");
    }
  }
};

export const updateFreeAnnouncement = async (userId: number, hasFreeAnnouncement: boolean): Promise<any> => {
  try {
    const token = localStorage.getItem("token")

    if (!token) {
      toast.error("Token non trouvé")
      return { status: 401, error: "Token non trouvé" }
    }

    const response = await axios.put(
      `${API_URL}/user/update-free-announcement/${userId}`,
      {
        hasFreeAnnouncement,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    )

    return response
  } catch (error) {
    console.error("Erreur lors de l'attribution du privilège d'annonce gratuite:", error)
    if (axios.isAxiosError(error) && error.response) {
      throw error.response.data
    } else {
      throw new Error("Une erreur inconnue s'est produite")
    }
  }
}

export const updateUserPassword = async ({
  userId,
  currentPassword,
  newPassword,
}: {
  userId: number;
  currentPassword: string;
  newPassword: string;
}) => {
  try {
    const token = localStorage.getItem("token");

    if (!token) {
      toast.error("Token non trouvé");
      return;
    }

    const response = await axios.post(
      `${API_URL}/user/update-password/${userId}`,
      {
        currentPassword,
        newPassword,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    toast.success("Mot de passe mis à jour avec succès !");
    return response.data;
  } catch (error) {
    console.error("❌ Erreur lors du changement de mot de passe :", error);
    toast.error("Erreur lors du changement de mot de passe.");
    throw error;
  }
};

export const getUsersWithOtherProfession = async (): Promise<UserModel[]> => {
  try {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("userId");

    if (!token || !userId) {
      console.warn("Utilisateur non connecté ou token manquant");
      return [];
    }

    const url = `${API_URL}/user/otherprofession`;

    const response = await axios.get(url, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      validateStatus: () => true,
    });

    // console.log("DEBUG getUsersWithOtherProfession - status:", response.status, "data:", response.data);

    if (response.status === 200 && Array.isArray(response.data)) {
      return response.data.map((u: any) =>
        ConvertUser.toUserModel(JSON.stringify(u))
      );


    } else {
      console.error("getUsersWithOtherProfession response non-200:", response.status, response.data);
      toast.error(response.data?.message || `Erreur ${response.status}`);
      return [];
    }
  } catch (error: any) {
    console.error("Erreur dans getUsersWithOtherProfession:", error);
    if (axios.isAxiosError(error) && error.response) {
      console.error("error.response.status:", error.response.status);
      console.error("error.response.data:", error.response.data);
    }
    return [];
  }
};

export const validateUser = async (userId: number) => {
  try {
    const token = localStorage.getItem("token");
    const response = await axios.put(`${API_URL}/user/${userId}/validate`, {}, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (err) {
    console.error("Erreur lors de la validation:", err);
    throw err;
  }
};

export const rejectUser = async (userId: number, reason: string) => {
  try {
    const token = localStorage.getItem("token");
    const response = await axios.post(
      `${API_URL}/user/${userId}/reject`,
      { reason },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (err) {
    console.error("Erreur lors du rejet:", err);
    throw err;
  }
};

export const downloadProfessionalDocument = async (documentPath: string) => {
  try {
    const response = await axios.get(`${API_URL}/user/document/${encodeURIComponent(documentPath)}`, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error('Erreur downloadProfessionalDocument:', error);
    throw error;
  }
};

// Nouvelle fonction pour l'inscription avec fichiers
export const registerUserWithFiles = async (formData: FormData) => {
  try {
    const response = await axios.post(`${API_URL}/user/signup-professional`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

export const updateUserLimits = async (
  userId: number,
  limits: { unpaid_annonce_limit: number; unpaid_amount_limit: number }
) => {
  try {
    const response = await axios.put(`${API_URL}/plafond/${userId}/limits`, limits, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Erreur lors de la mise à jour des plafonds", error);
    throw error;
  }
};

export const getClientWithLimits = async (userId: number) => {
  try {
    const response = await axios.get(`${API_URL}/plafond/clients/${userId}/limits`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Erreur lors de la récupération du client avec limites", error);
    throw error;
  }
};

export const resetUserLimits = async (userId: number) => {
  try {
    const response = await axios.put(`${API_URL}/plafond/${userId}/reset`, null, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Erreur lors de la réinitialisation du compteur", error);
    throw error;
  }
};

export const updateUserPoints = async (
  userId: number,
  earnedPoints: number,
  usedPoints: number
) => {
  const response = await axios.post(
    `${API_URL}/user/${userId}/update-points`,
    { earnedPoints, usedPoints },
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    }
  );

  return response.data;
};

export const EmailAutorized = async (userId: number) => {
  try {
    const response = await axios.post(
      `${API_URL}/user/email_autorized/${userId}`
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

/**
 * Permet à un admin de se connecter en tant qu'utilisateur
 * @param userId - ID de l'utilisateur cible
 */
export const loginAsUser = async (userId: number): Promise<{ success: boolean; user: UserModel; token: string }> => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('Token non trouvé');
    }

    const response = await axios.post(
      `${API_URL}/user/login-as-user/${userId}`,
      {},
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      }
    );

    if (response.status === 200 && response.data.success) {
      const { user, token: newToken } = response.data;
      
      // Sauvegarder les informations de l'utilisateur dans le localStorage
      saveUserToLocalStorage(user, newToken);
      
      return {
        success: true,
        user,
        token: newToken
      };
    } else {
      throw new Error(response.data.message || 'Erreur lors de la connexion');
    }
  } catch (error: any) {
    console.error('Erreur loginAsUser:', error);
    
    if (axios.isAxiosError(error) && error.response) {
      toast.error(error.response.data.message || 'Erreur lors de la connexion');
      throw new Error(error.response.data.message);
    } else {
      toast.error('Une erreur est survenue');
      throw error;
    }
  }
};
